import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import useDeviceType from "../../../custom-hooks/DeviceType";
import EventEHRSwitchButton from "../../../component-lib/Molecules/switch-button/EventEHRSwitchButton";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import MenuDropdown from "../../../component-lib/Molecules/menuDropdown/MenuDropdown";
import WrapperCard from "../../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import SearchSelectDropdown from "../../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import {
  DownloadIcon,
  plusIcon,
  rightDirectionBlackIcon,
  searchIcon,
} from "../../../assets";
import Button from "../../../component-lib/Molecules/button/Button";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import DrugCatalogueDialog from "../dialogs/DrugCatalogueDialog";
import {
  deleteDrug,
  getDrugList,
  patchDrugData,
  postDrugData,
} from "../SettingsSaga";
import { componentKey, setCloseAddDrugDialog } from "../SettingsSlice";
import EventEHRConfirmationDialog from "../../../component-lib/Molecules/confimation-dialog/EventEHRConfirmationDialog";

const DrugCatalogue = () => {
  const { drugList, closeAddDrugDialog } = useSelector(
    (state: any) => state[componentKey]
  );

  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [rowPerPage, setRowPerPage] = useState("10");
  const [pageNumber, setPageNumber] = useState(1);
  const [searchedValue, setSearchValue] = useState<string>("");
  const [status, setStatus] = useState("");
  const [defaultDrugDataRow, setDefaultDrugDataRow] = useState<any>({});
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [selectedDrugId, setSelectedDrugId] = useState<string | null>(null);

  // OPEN ADD/EDIT DRUG DIALOG
  const [dialogState, setDialogState] = useState<null | "add" | "edit">(null);

  // OPEN DRUG CATALOGUE DIALOG
  const [openDugCatalogueDialog, setOpenDugCatalogueDialog] = useState(false);

  const deviceType = useDeviceType();

  //DRUG CATALOGUE COLUMN DATA
  const DrugCatalogueColumns = [
    {
      field: "drugtype",
      label: <div>Drug type</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.drugtype}</p>;
      },
    },
    {
      field: "drugName",
      label: <div>Drug name</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.drugName}</p>;
      },
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => {
        return (
          <>
            <div className="flex items-center">
              <MenuDropdown
                options={[
                  {
                    label: "Edit Drug",
                    onClickCb: () => {
                      setDefaultDrugDataRow(row);
                      setDialogState("edit"); // Open edit dialog
                      setOpenDugCatalogueDialog(true); // Ensure dialog opens
                    },
                  },
                  {
                    label: "Delete Drug",
                    onClickCb: () => {
                      setSelectedDrugId(row?.id);
                      setOpenConfirmationDialog(true);
                    },
                  },
                ]}
              />
            </div>
          </>
        );
      },
    },
  ];

  // HANDLE SEARCH CHANGE VALUE AND ADDED SOME DELAY
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(1);

    const value = e.target.value;

    // CLEAR THE PREVIOUS TIMEOUT
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // SET A NEW TIMEOUT TO DELAY THE CALLBACK
    typingTimeoutRef.current = setTimeout(() => {
      setSearchValue(value?.trim());
    }, 500); // ADJUST DELAY AS NEEDED
  };

  //DRUG CATALOGUE ROW DATA

  const drugRowData = drugList?.medicine?.map((data: any) => ({
    id: data?.id || "-",
    drugName: data?.medicineName || "-",
    drugtype: data?.medicineType || "-",
  }));

  useEffect(() => {
    getDrug();
  }, [searchedValue, rowPerPage, pageNumber]);

  useEffect(() => {
    if (closeAddDrugDialog) {
      dispatch(setCloseAddDrugDialog(false));
      setDialogState(null);
      setOpenDugCatalogueDialog(false); // Ensure dialog closes
      getDrug();
      setOpenConfirmationDialog(false); // for delete drug confirmation dialog(for api failing scenario)
    }
  }, [closeAddDrugDialog]);

  const getDrug = () => {
    const payload = {
      pageNumber: pageNumber,
      limit: rowPerPage,
      medicineName: searchedValue?.trim(),
    };
    dispatch(getDrugList(payload));
  };

  const addDrug = (data: any) => {
    console.log("data:", data);

    try {
      const payload = {
        medicineType: data?.drugType,
        medicineName: data?.drugName,
      };
      if (defaultDrugDataRow?.drugName) {
        const drugUpdatePayload = {
          payload: {
            ...payload,
          },
          drugId: defaultDrugDataRow?.id,
        };
        dispatch(patchDrugData(drugUpdatePayload));
      } else {
        dispatch(postDrugData(payload));
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <WrapperCard
      customClasses="m-3 p-3 flex-1 min-h-0"
      customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="flex flex-col h-full gap-3">
        <div
          className={`flex ${deviceType === "mobile" ? "flex-col items-start gap-3" : "items-center"} justify-between w-full`}
        >
          <div className="flex items-center gap-3">
            <img
              className="cursor-pointer"
              src={rightDirectionBlackIcon}
              alt="back-arrow"
              style={{ height: "12.5px" }}
              onClick={() => {
                navigate("/settings");
              }}
            />
            <Heading
              title="Drug Catalogue"
              customStyle={{ color: "#1D1D1D" }}
            />
          </div>

          <div
            className={`flex ${deviceType === "mobile" ? "w-full justify-between" : ""} gap-3`}
          >
            <div style={{ maxWidth: "350px" }}>
              <SearchSelectDropdown
                freeSolo={true}
                searchedValue={searchedValue}
                onSearchValueChangeCb={(e) => handleChange(e)}
                size="small"
                label="Search by drug name"
                fullWidth={deviceType === "mobile" ? "160px" : "300px"}
                startIcon={
                  <img
                    alt="search-icon"
                    src={searchIcon}
                    width={"18px"}
                    height={"18px"}
                  />
                }
              />
            </div>
            {/* <div>
              <SelectDropdown
                label=""
                name="status"
                placeholderValue="All Status"
                displayEmpty={true}
                value={status}
                options={[
                  { label: "Active", value: "Active" },
                  { label: "In Active", value: "InActive" },
                ]}
                onChangeCb={(e) => {
                  setStatus(e.target.value);
                }}
              />
            </div> */}

            {/* <Button
              startIcon={
                <img
                  src={DownloadIcon}
                  alt={"search"}
                  className="cursor-pointer w-[19px] h-[19px]"
                />
              }
              label="Import CSV"
              variant="outlined"
              onClickCb={() => {}}
              style={{
                paddingTop: "6px",
                paddingBottom: "6px",
                minWidth: "155px",
                color: "#727272",
                border: "1px solid #D2D2D2",
                backgroundColor: "#FFFFFF",
                textTransform: "none",
                boxShadow: "none",
              }}
            ></Button> */}
            <Button
              startIcon={
                <img
                  src={plusIcon}
                  alt={"search"}
                  className="cursor-pointer w-[19px] h-[19px]"
                />
              }
              label="Add Drug"
              variant="contained"
              onClickCb={() => {
                setDefaultDrugDataRow({});
                setDialogState("add"); // Open add dialog
                setOpenDugCatalogueDialog(true); // Ensure dialog opens
              }}
              style={{
                paddingTop: "6px",
                paddingBottom: "6px",
                minWidth: "155px",
              }}
            ></Button>
          </div>
        </div>
        <div style={{ overflow: "auto" }}>
          <Table
            columns={DrugCatalogueColumns}
            rows={drugRowData}
            paginationProps={{
              isPagination: true,
              page: pageNumber,
              totalPages: drugList?.totalPages,
              totalCount: drugList?.total,
              limit: rowPerPage,
              rowsPerPageOptions: [10, 25, 50],
              onPageChange: (page: any) => {
                setPageNumber(page);
              },
              handleChangeRowsPerPage: (rowPerPage: any) => {
                setRowPerPage(rowPerPage);
                setPageNumber(1);
              },
            }}
          />
        </div>
      </div>
      {/* ADD DRUG DIALOG */}
      {openDugCatalogueDialog && (
        <DrugCatalogueDialog
          defaultDrugData={defaultDrugDataRow}
          isEdit={dialogState === "edit"} // Determine edit mode
          isOpen={!!dialogState} // Dialog is open if dialogState is not null          isOpen={openCodeDialog}
          handleClose={() => {
            setDialogState(null); // Close the dialog by setting to null
            setOpenDugCatalogueDialog(false); // Ensure dialog closes
          }}
          handleData={(data) => {
            addDrug(data);
            console.log("data:", data);
          }}
        />
      )}

      {openConfirmationDialog && (
        <EventEHRConfirmationDialog
          alertTitle="Delete Drug"
          alertDescription={`Are you sure you want to delete this Drug Details? Please confirm to proceed.`}
          cancelButtonTitle="Cancel"
          submitButtonTitle="Delete"
          open={openConfirmationDialog}
          handleClose={(action) => {
            if (action === "close") {
              setOpenConfirmationDialog(false);
            } else if (action === "submit") {
              if (selectedDrugId) {
                const payload = {
                  drugId: selectedDrugId,
                  comesFrom: "",
                };
                dispatch(deleteDrug(payload));
              }
              // setOpenConfirmationDialog(false);
            }
          }}
        />
      )}
    </WrapperCard>
  );
};

export default DrugCatalogue;
